import { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { StickyNote, XCircle, Package, GripVertical, Loader2, Truck, Repeat, Store } from "lucide-react";
import { toast } from "sonner";
import { AdminReturnRequest } from "../components/ReturnRequest";

const COLUMNS = [
  { key: "placed", label: "Placed", cls: "text-terracotta" },
  { key: "confirmed", label: "Confirmed", cls: "text-sage" },
  { key: "packed", label: "Packed", cls: "text-ochre" },
  { key: "shipped", label: "Shipped", cls: "text-ochre" },
  { key: "delivered", label: "Delivered", cls: "text-sage" },
  { key: "cancelled", label: "Cancelled", cls: "text-destructive" },
];

export default function AdminOrderBoardPage() {
  const [orders, setOrders] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [panel, setPanel] = useState(null); // `${orderId}|notes|track|reassign`
  const [noteText, setNoteText] = useState("");
  const [trackText, setTrackText] = useState("");
  const [reassignData, setReassignData] = useState({});
  const [reassignSel, setReassignSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  if (!orders) return <div className="text-charcoal-muted">Loading board…</div>;

  const move = async (order, status) => {
    if (order.status === status) return;
    const prev = orders;
    setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, status } : o)));
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status });
      toast.success(`${order.short_id} → ${status}`);
    } catch (e) {
      setOrders(prev);
      toast.error(e.response?.data?.detail || "Could not update status");
    }
  };

  const cancel = async (order) => {
    if (!window.confirm(`Cancel order ${order.short_id}?`)) return;
    const prev = orders;
    setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, status: "cancelled" } : o)));
    try {
      await api.post(`/admin/orders/${order.id}/cancel`);
      toast.success(`${order.short_id} cancelled`);
    } catch (e) {
      setOrders(prev);
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };

  const openPanel = async (o, type) => {
    const key = `${o.id}|${type}`;
    if (panel === key) { setPanel(null); return; }
    setPanel(key);
    setNoteText("");
    if (type === "track") setTrackText(o.tracking_link || "");
    if (type === "reassign" && !reassignData[o.id]) {
      try {
        const { data } = await api.get(`/admin/orders/${o.id}/reassign-options`);
        setReassignData((d) => ({ ...d, [o.id]: data.items }));
      } catch (e) { toast.error("Could not load reassignment options"); }
    }
  };

  const addNote = async (order) => {
    const text = noteText.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/notes`, { text });
      setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, admin_notes: [...(o.admin_notes || []), data.note] } : o)));
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
    finally { setBusy(false); }
  };

  const saveTracking = async (order) => {
    const link = trackText.trim();
    try {
      await api.patch(`/admin/orders/${order.id}/tracking`, { tracking_link: link || null });
      setOrders((os) => os.map((o) => (o.id === order.id ? { ...o, tracking_link: link || null } : o)));
      toast.success("Tracking link saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save tracking link"); }
  };

  const doReassign = async (order, itemIndex) => {
    const sel = reassignSel[`${order.id}|${itemIndex}`];
    if (!sel) { toast.error("Choose a seller first"); return; }
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/reassign`, { item_index: itemIndex, new_seller_id: sel });
      toast.success(`Reassigned to ${data.business_name}`);
      setReassignData((d) => { const n = { ...d }; delete n[order.id]; return n; });
      setPanel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reassign"); }
  };

  return (
    <div data-testid="admin-order-board">
      <div className="mb-4">
        <h2 className="font-heading font-semibold text-2xl text-charcoal">Order Workflow Board</h2>
        <p className="text-sm text-charcoal-muted mt-1">Drag an order to a new stage, add notes, set a tracking link, reassign the seller, or cancel.</p>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const colOrders = orders.filter((o) => o.status === col.key);
          return (
            <div
              key={col.key}
              data-testid={`board-col-${col.key}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
              onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
              onDrop={(e) => {
                e.preventDefault();
                setOverCol(null);
                const id = e.dataTransfer.getData("text/plain");
                const ord = orders.find((o) => o.id === id);
                if (ord) move(ord, col.key);
              }}
              className={`shrink-0 w-80 bg-off-white-alt border transition-colors ${overCol === col.key ? "border-terracotta ring-1 ring-terracotta" : "border-border"}`}
            >
              <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-off-white-alt sticky top-0 z-10">
                <span className={`text-xs uppercase tracking-widest font-bold ${col.cls}`}>{col.label}</span>
                <span className="text-xs text-charcoal-muted bg-white border border-border px-1.5">{colOrders.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[140px]">
                {colOrders.map((o) => (
                  <div
                    key={o.id}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", o.id); setDragId(o.id); }}
                    onDragEnd={() => setDragId(null)}
                    data-testid={`order-card-${o.short_id}`}
                    className={`bg-white border border-border p-3 cursor-grab active:cursor-grabbing ${dragId === o.id ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-bold text-charcoal text-sm">{o.short_id}</div>
                        <div className="text-[11px] text-charcoal-muted truncate">{o.address?.name} · {o.address?.pincode}</div>
                      </div>
                      <GripVertical className="w-4 h-4 text-charcoal-muted shrink-0" />
                    </div>

                    {/* Seller identity — always visible */}
                    <div className="mt-2 space-y-0.5 bg-off-white-alt border border-border p-1.5" data-testid={`card-sellers-${o.short_id}`}>
                      {(o.sellers || []).map((s) => (
                        <div key={s.id} className="text-[11px] text-charcoal flex items-center gap-1">
                          <Store className="w-3 h-3 text-terracotta shrink-0" />
                          <span className="font-medium truncate">{s.business_name}</span>
                          <span className="text-charcoal-muted whitespace-nowrap">· {s.phone || "no phone"}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-1 text-[11px] text-charcoal-muted mt-2">
                      <Package className="w-3 h-3" /> {o.items?.length} item(s) · <span className="uppercase">{o.payment_method}</span>
                    </div>
                    <div className="font-heading font-bold text-charcoal text-sm mt-1">{inr(o.total)}</div>
                    {o.tracking_link && (
                      <a href={o.tracking_link} target="_blank" rel="noreferrer" className="text-[11px] text-terracotta hover:underline inline-flex items-center gap-1 mt-1" data-testid={`card-tracking-link-${o.short_id}`}>
                        <Truck className="w-3 h-3" /> Tracking link
                      </a>
                    )}

                    <AdminReturnRequest order={o} onUpdated={load} />

                    <select
                      value={o.status}
                      onChange={(e) => move(o, e.target.value)}
                      data-testid={`card-status-${o.short_id}`}
                      className="w-full mt-2 border border-border bg-off-white text-xs px-2 py-1 outline-none focus:border-terracotta"
                    >
                      {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>

                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <button onClick={() => openPanel(o, "notes")} data-testid={`card-notes-btn-${o.short_id}`} className="text-[11px] inline-flex items-center gap-1 border border-border px-2 py-1 hover:border-terracotta transition-colors">
                        <StickyNote className="w-3 h-3" /> Notes{o.admin_notes?.length ? ` (${o.admin_notes.length})` : ""}
                      </button>
                      <button onClick={() => openPanel(o, "track")} data-testid={`card-track-btn-${o.short_id}`} className="text-[11px] inline-flex items-center gap-1 border border-border px-2 py-1 hover:border-terracotta transition-colors">
                        <Truck className="w-3 h-3" /> Track
                      </button>
                      <button onClick={() => openPanel(o, "reassign")} data-testid={`card-reassign-btn-${o.short_id}`} className="text-[11px] inline-flex items-center gap-1 border border-border px-2 py-1 hover:border-terracotta transition-colors">
                        <Repeat className="w-3 h-3" /> Change Seller
                      </button>
                      {o.status !== "cancelled" && o.status !== "delivered" && (
                        <button onClick={() => cancel(o)} data-testid={`cancel-order-${o.short_id}`} className="text-[11px] inline-flex items-center gap-1 border border-destructive/40 text-destructive px-2 py-1 hover:bg-destructive/10 transition-colors">
                          <XCircle className="w-3 h-3" /> Cancel
                        </button>
                      )}
                    </div>

                    {panel === `${o.id}|notes` && (
                      <div className="mt-2 border-t border-border pt-2" data-testid={`notes-panel-${o.short_id}`}>
                        <div className="space-y-1 max-h-32 overflow-y-auto mb-2">
                          {(o.admin_notes || []).length === 0 ? (
                            <div className="text-[11px] text-charcoal-muted">No notes yet.</div>
                          ) : (o.admin_notes || []).map((n) => (
                            <div key={n.id} className="text-[11px] bg-off-white-alt border border-border p-1.5">
                              <div className="text-charcoal">{n.text}</div>
                              <div className="text-charcoal-muted mt-0.5">{n.by_name} · {new Date(n.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-1">
                          <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(o); }} placeholder="Add a note…" data-testid={`note-input-${o.short_id}`} className="flex-1 border border-border bg-off-white text-[11px] px-2 py-1 outline-none focus:border-terracotta" />
                          <button onClick={() => addNote(o)} disabled={busy} data-testid={`add-note-${o.short_id}`} className="text-[11px] bg-terracotta text-off-white px-2 py-1 disabled:opacity-50">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}</button>
                        </div>
                      </div>
                    )}

                    {panel === `${o.id}|track` && (
                      <div className="mt-2 border-t border-border pt-2" data-testid={`track-panel-${o.short_id}`}>
                        <label className="block text-[10px] uppercase tracking-widest text-charcoal-muted mb-1">Delivery tracking link (any provider)</label>
                        <div className="flex gap-1">
                          <input value={trackText} onChange={(e) => setTrackText(e.target.value)} placeholder="https://…" data-testid={`track-input-${o.short_id}`} className="flex-1 border border-border bg-off-white text-[11px] px-2 py-1 outline-none focus:border-terracotta" />
                          <button onClick={() => saveTracking(o)} data-testid={`save-track-${o.short_id}`} className="text-[11px] bg-terracotta text-off-white px-2 py-1">Save</button>
                        </div>
                      </div>
                    )}

                    {panel === `${o.id}|reassign` && (
                      <div className="mt-2 border-t border-border pt-2 space-y-2" data-testid={`reassign-panel-${o.short_id}`}>
                        {!reassignData[o.id] ? (
                          <div className="text-[11px] text-charcoal-muted inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading options…</div>
                        ) : reassignData[o.id].map((row) => (
                          <div key={row.item_index} className="text-[11px] border border-border p-2">
                            <div className="font-medium text-charcoal truncate">{row.title}</div>
                            <div className="text-charcoal-muted mb-1">Current: {row.current_seller_name}{row.current_seller_phone ? ` · ${row.current_seller_phone}` : ""}</div>
                            {row.options.length === 0 ? (
                              <div className="text-charcoal-muted">No other sellers stock this product.</div>
                            ) : (
                              <div className="flex gap-1">
                                <select
                                  value={reassignSel[`${o.id}|${row.item_index}`] || ""}
                                  onChange={(e) => setReassignSel((s) => ({ ...s, [`${o.id}|${row.item_index}`]: e.target.value }))}
                                  data-testid={`reassign-select-${o.short_id}-${row.item_index}`}
                                  className="flex-1 border border-border bg-off-white text-[11px] px-1 py-1 outline-none focus:border-terracotta"
                                >
                                  <option value="">Choose seller…</option>
                                  {row.options.map((op) => (
                                    <option key={op.seller_id} value={op.seller_id}>
                                      {op.business_name}{op.phone ? ` · ${op.phone}` : ""}{op.verified ? " ✓" : ""}
                                    </option>
                                  ))}
                                </select>
                                <button onClick={() => doReassign(o, row.item_index)} data-testid={`reassign-btn-${o.short_id}-${row.item_index}`} className="text-[11px] bg-terracotta text-off-white px-2 py-1">Reassign</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {colOrders.length === 0 && <div className="text-[11px] text-charcoal-muted text-center py-6 border border-dashed border-border">Drop orders here</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
